import { createContext } from "react";
import type { HubConnection } from "@microsoft/signalr";

// ---------------------------------------------------------------------------
// Connection state taxonomy
//
// The SignalR client exposes a richer state machine
// (`Disconnected | Connecting | Connected | Disconnecting | Reconnecting`)
// than the UI cares about. We collapse it to the three states the header
// indicator distinguishes — anything ambiguous folds into "reconnecting"
// so the UI never reads as "connected" while the wire is mid-flap.
// ---------------------------------------------------------------------------

export type RealtimeConnectionState = "connected" | "reconnecting" | "disconnected";

// ---------------------------------------------------------------------------
// Two channels, two cadences
//
// The hub connection object changes once per session (null → connection).
// Its state ticks on every reconnect. Handing both through one context value
// made every hub subscriber re-render on each tick — a storm at startup
// (disconnected → reconnecting → connected) and again on every network
// flap. The connection travels through React context; the state lives in an
// external store that only `useSyncExternalStore` readers (the status chip)
// and imperative subscribers (group re-join on "connected") observe.
// ---------------------------------------------------------------------------

/** Connection and state as one value: the input shape of `RealtimeContextProvider`. */
export interface RealtimeContextValue {
    readonly connection: HubConnection | null;
    readonly state: RealtimeConnectionState;
}

export interface RealtimeStateStore {
    readonly getSnapshot: () => RealtimeConnectionState;
    /** Fires after each change. Returns the unsubscribe function. */
    readonly subscribe: (listener: () => void) => () => void;
}

export interface MutableRealtimeStateStore extends RealtimeStateStore {
    readonly set: (next: RealtimeConnectionState) => void;
}

export function createRealtimeStateStore(
    initial: RealtimeConnectionState = "disconnected",
): MutableRealtimeStateStore {
    let current = initial;
    const listeners = new Set<() => void>();
    return {
        getSnapshot: () => current,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        set: (next) => {
            if (next === current) return;
            current = next;
            for (const listener of Array.from(listeners)) {
                listener();
            }
        },
    };
}

// Sentinel defaults. The `null` connection is what hooks see when they run
// outside the provider (most notably, tests that mount a single component
// without wrapping it). Hooks branch on `connection === null` rather than
// throwing — realtime is advisory, and an unwired test should degrade
// silently to "the cache will be invalidated by the next REST refetch", not
// crash. The default store never leaves "disconnected".
export const RealtimeConnectionContext = createContext<HubConnection | null>(null);

export const RealtimeStateStoreContext = createContext<RealtimeStateStore>(
    createRealtimeStateStore("disconnected"),
);
